import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { MdLocalShipping, MdSearch, MdFilterList } from 'react-icons/md';
import { LoadingPage } from 'components/Loading';
import { fretesAPI, viagensAPI, cargasAPI, mdfeAPI, rastreamentoAPI } from 'services/apiRest';
import { actions as toastrActions } from 'react-redux-toastr';
import ChargeItem from 'pages/Convocations/components/ChargeItem';
import AnimatedSearchInput from 'components/AnimatedSearchInput';
import ModalFilter from 'pages/Convocations/components/ModalFilter';
import StaticDrawer from 'components/StaticDrawer';
import { MapConvocation } from 'pages/Convocations/components/MapConvocation';
import MdfeMenuSimplificado from './components/MdfeMenuSimplificado';
import Images from 'assets/img';
import CustomScrollbar from 'react-custom-scrollbars';
import {
  Container,
  WrapperTable,
  Actions,
  Action,
  Card,
  BoxMap,
} from './styles';

export default function AcompanhamentoCarga() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { empresa, user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [cargas, setCargas] = useState([]);
  const [filteredCargas, setFilteredCargas] = useState([]);
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCarga, setSelectedCarga] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [relatedTrips, setRelatedTrips] = useState([]); // Viagens relacionadas à carga selecionada
  const [allTrips, setAllTrips] = useState([]); // Todas as viagens das cargas em andamento
  const [allTrackingData, setAllTrackingData] = useState([]); // Todas as últimas localizações de todas as cargas
  const [filteredTrackingData, setFilteredTrackingData] = useState([]); // Localizações filtradas por carga selecionada

  const [formFilter, setFormFilter] = useState({
    id: '',
    startDate: new Date('01/01/2000'),
    endDate: new Date(),
    status: [],
    driver_name: [],
    type: [],
    vehicle: '',
    nfe: '',
    delivery_city: [],
    delivery_state: [],
  });

  useEffect(() => {
    carregarCargas();
  }, [empresa?.id, user?.id]);

  const carregarCargas = async () => {
    try {
      setLoading(true);

      // 1. Buscar fretes do embarcador logado
      const fretes = await fretesAPI.listar({ empresa_id: empresa?.id });
      
      // 2. Buscar viagens geradas a partir desses fretes
      const todasViagens = await viagensAPI.listar({ empresa_id: empresa?.id });
      const viagensDeFretes = todasViagens.filter(v => {
        if (!v.note || !v.note.includes('[FRETE_ID:')) return false;
        
        // Extrair frete_id da nota
        const match = v.note.match(/\[FRETE_ID:([^\]]+)\]/);
        if (!match || !match[1]) return false;
        
        const freteId = match[1];
        // Verificar se o frete pertence ao embarcador
        return fretes.some(f => f.id === freteId);
      });

      // 3. Buscar cargas relacionadas a essas viagens (não encerradas)
      // A relação é: viagem.carga_id aponta para carga.id
      const cargaIds = viagensDeFretes
        .map(v => v.carga_id)
        .filter(id => id != null); // Remover nulls/undefined
      
      // Buscar cargas pelos IDs encontrados
      const cargasRelacionadas = [];
      for (const cargaId of cargaIds) {
        try {
          const carga = await cargasAPI.buscarPorId(cargaId);
          if (carga) {
            // Filtrar apenas cargas não encerradas
            const statusEncerrado = ['encerrada', 'finalizada', 'concluida', 'encerrado', 'finalizado', 'concluido', 'completed'];
            if (carga.status && statusEncerrado.includes(carga.status.toLowerCase())) {
              continue; // Pular cargas encerradas
            }
            
            // Verificar se tem data de encerramento
            if (carga.data_encerramento) {
              continue; // Pular cargas com data de encerramento
            }
            
            cargasRelacionadas.push(carga);
          }
        } catch (error) {
          console.warn(`Erro ao buscar carga ${cargaId}:`, error);
        }
      }
      
      // Remover duplicatas (caso várias viagens apontem para a mesma carga)
      const cargasUnicas = Array.from(
        new Map(cargasRelacionadas.map(c => [c.id, c])).values()
      );

      // 4. Enriquecer cargas com dados do frete e viagem
      const cargasEnriquecidas = cargasUnicas.map(carga => {
        // Encontrar viagens que apontam para esta carga
        const viagensRelacionadas = viagensDeFretes.filter(v => v.carga_id === carga.id);
        const viagem = viagensRelacionadas[0]; // Pegar a primeira viagem relacionada
        const freteId = viagem?.note?.match(/\[FRETE_ID:([^\]]+)\]/)?.[1];
        const frete = freteId ? fretes.find(f => f.id === freteId) : null;

        return {
          ...carga,
          frete_numero: frete?.numero || null,
          viagem_id: viagem?.id || null,
        };
      });

      setCargas(cargasEnriquecidas);
      setFilteredCargas(cargasEnriquecidas);

      // 5. Buscar todas as viagens das cargas em andamento para exibir no mapa
      const todasViagensIds = cargasUnicas
        .map(c => c.id)
        .filter(id => id != null);
      
      if (todasViagensIds.length > 0) {
        try {
          // Buscar todas as viagens relacionadas a essas cargas
          const todasViagens = await Promise.all(
            todasViagensIds.map(async (cargaId) => {
              try {
                const viagens = await viagensAPI.listar({ carga_id: cargaId });
                return viagens || [];
              } catch (error) {
                console.warn(`Erro ao buscar viagens da carga ${cargaId}:`, error);
                return [];
              }
            })
          );
          
          // Flatten do array de arrays
          const viagensFlatten = todasViagens.flat();
          
          // Filtrar apenas viagens que têm coordenadas do destinatário
          const viagensComCoordenadas = viagensFlatten.filter(v => {
            const lat = v.destinatario?.lat || v.destinatario_lat;
            const lng = v.destinatario?.lng || v.destinatario_lng;
            return lat && lng && parseFloat(lat) !== 0 && parseFloat(lng) !== 0;
          });
          
          console.log(`📍 [AcompanhamentoCarga] Total de viagens carregadas para o mapa: ${viagensComCoordenadas.length} de ${viagensFlatten.length}`);
          setAllTrips(viagensComCoordenadas);
        } catch (error) {
          console.error('Erro ao carregar viagens para o mapa:', error);
          setAllTrips([]);
        }
      } else {
        setAllTrips([]);
      }

      // 6. Buscar últimas localizações de rastreamento APENAS das cargas do embarcador
      try {
        console.log(`📍 [AcompanhamentoCarga] Buscando últimas localizações das cargas do embarcador...`);
        console.log(`📋 [AcompanhamentoCarga] IDs das cargas do embarcador:`, cargasUnicas.map(c => c.id));
        
        // Criar um Set com os IDs das cargas do embarcador para busca rápida
        const cargasIdsSet = new Set(cargasUnicas.map(c => c.id));
        
        // Buscar todas as últimas localizações diretamente do banco
        const todasLocalizacoes = await rastreamentoAPI.buscarUltimasLocalizacoesTodasCargas();
        console.log(`📊 [AcompanhamentoCarga] Total de localizações encontradas no banco: ${todasLocalizacoes.length}`);
        
        // Filtrar e normalizar os dados - APENAS das cargas do embarcador
        const trackingDataFiltered = todasLocalizacoes
          .filter(track => {
            if (!track) return false;
            
            // FILTRO PRINCIPAL: Verificar se a carga pertence ao embarcador
            if (!track.carga_id || !cargasIdsSet.has(track.carga_id)) {
              return false; // Pular cargas que não pertencem ao embarcador
            }
            
            // Normalizar campos de latitude/longitude
            const lat = track.latitude || track.lat;
            const lng = track.longitude || track.lng;
            
            if (!lat || !lng) {
              console.log(`⚠️ [AcompanhamentoCarga] Registro sem coordenadas:`, track);
              return false;
            }
            
            const latNum = parseFloat(lat);
            const lngNum = parseFloat(lng);
            
            // Validar coordenadas
            if (isNaN(latNum) || isNaN(lngNum) || latNum === 0 || lngNum === 0) {
              return false;
            }
            
            // Validar se está dentro de limites razoáveis (Brasil)
            if (latNum < -35 || latNum > 6 || lngNum < -75 || lngNum > -30) {
              console.log(`⚠️ [AcompanhamentoCarga] Coordenadas fora dos limites:`, { lat: latNum, lng: lngNum, carga_id: track.carga_id });
              return false;
            }
            
            return true;
          })
          .map(track => ({
            ...track,
            latitude: parseFloat(track.latitude || track.lat),
            longitude: parseFloat(track.longitude || track.lng),
          }));
        
        console.log(`✅ [AcompanhamentoCarga] ${trackingDataFiltered.length} localizações válidas das cargas do embarcador de ${todasLocalizacoes.length} encontradas no banco`);
        console.log(`📊 [AcompanhamentoCarga] Dados de rastreamento finais (filtrados por embarcador):`, trackingDataFiltered);
        
        setAllTrackingData(trackingDataFiltered);
      } catch (error) {
        console.error('❌ [AcompanhamentoCarga] Erro ao carregar dados de rastreamento:', error);
        setAllTrackingData([]);
      }
    } catch (error) {
      console.error('Erro ao carregar cargas:', error);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao carregar cargas para acompanhamento.',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    aplicarFiltros(value, formFilter);
  };

  const aplicarFiltros = (searchValue = search, filters = formFilter) => {
    let dataFiltered = [...cargas];

    // Filtro de busca por texto
    if (searchValue) {
      dataFiltered = dataFiltered.filter(
        (item) =>
          (item.id || '').toString().toUpperCase().includes(searchValue.toUpperCase()) ||
          (item.codigo || '').toString().toUpperCase().includes(searchValue.toUpperCase()) ||
          (item.driver_name || '').toUpperCase().includes(searchValue.toUpperCase()) ||
          (item.vehicle || '').toUpperCase().includes(searchValue.toUpperCase())
      );
    }

    // Filtro por ID
    if (filters.id) {
      dataFiltered = dataFiltered.filter(
        (item) =>
          item.id != null &&
          String(item.id || '').toUpperCase().includes(filters.id.toUpperCase())
      );
    }

    // Filtro por NFe
    if (filters.nfe) {
      dataFiltered = dataFiltered.filter(
        (item) =>
          item.nfe != null &&
          String(item.nfe || '').toUpperCase().includes(filters.nfe.toUpperCase())
      );
    }

    // Filtro por veículo
    if (filters.vehicle) {
      dataFiltered = dataFiltered.filter(
        (item) =>
          item.vehicle != null &&
          String(item.vehicle || '').toUpperCase().includes(filters.vehicle.toUpperCase())
      );
    }

    // Filtro por tipo
    if (filters.type && filters.type.length > 0) {
      const filteredData = [];
      filters.type.forEach((typ) => {
        filteredData.push(
          ...dataFiltered.filter(
            (item) =>
              item.type != null &&
              String(item.type || '').toUpperCase().includes(String(typ.value || '').toUpperCase())
          )
        );
      });
      dataFiltered = filteredData;
    }

    // Filtro por status
    if (filters.status && filters.status.length > 0) {
      const filteredData = [];
      filters.status.forEach((stats) => {
        filteredData.push(
          ...dataFiltered.filter(
            (item) =>
              item.status != null &&
              String(item.status || '').toUpperCase() === String(stats.value || '').toUpperCase()
          )
        );
      });
      dataFiltered = filteredData;
    }

    // Filtro por motorista
    if (filters.driver_name && filters.driver_name.length > 0) {
      const filteredData = [];
      filters.driver_name.forEach((driver) => {
        filteredData.push(
          ...dataFiltered.filter(
            (item) =>
              item.driver_name != null &&
              String(item.driver_name || '').toUpperCase() === String(driver.value || '').toUpperCase()
          )
        );
      });
      dataFiltered = filteredData;
    }

    // Filtro por estado de entrega
    if (filters.delivery_state && filters.delivery_state.length > 0) {
      const filteredData = [];
      filters.delivery_state.forEach((state) => {
        filteredData.push(
          ...dataFiltered.filter(
            (item) =>
              item.delivery_state != null &&
              String(item.delivery_state || '').toUpperCase().includes(String(state || '').toUpperCase())
          )
        );
      });
      dataFiltered = filteredData;
    }

    // Filtro por cidade de entrega
    if (filters.delivery_city && filters.delivery_city.length > 0) {
      const filteredData = [];
      filters.delivery_city.forEach((city) => {
        filteredData.push(
          ...dataFiltered.filter(
            (item) =>
              item.delivery_city != null &&
              String(item.delivery_city || '').toUpperCase().includes(String(city || '').toUpperCase())
          )
        );
      });
      dataFiltered = filteredData;
    }

    // Filtro por data
    if (filters.startDate && filters.endDate) {
      const selectedDateOne = new Date(filters.startDate);
      const selectedDateTwo = new Date(filters.endDate);
      selectedDateOne.setHours(0, 0, 0);
      selectedDateTwo.setHours(23, 59, 59);

      dataFiltered = dataFiltered.filter((item) => {
        if (!item.created_at) return false;
        const itemDate = new Date(item.created_at);
        return itemDate >= selectedDateOne && itemDate <= selectedDateTwo;
      });
    }

    setFilteredCargas(dataFiltered);
  };

  const handleFilter = (filters) => {
    setFormFilter(filters);
    aplicarFiltros(search, filters);
    setFilterOpen(false);
  };

  const handleResetFilter = () => {
    const resetFilters = {
      id: '',
      startDate: new Date('01/01/2000'),
      endDate: new Date(),
      status: [],
      driver_name: [],
      type: [],
      vehicle: '',
      nfe: '',
      delivery_city: [],
      delivery_state: [],
    };
    setFormFilter(resetFilters);
    setSearch('');
    setFilteredCargas(cargas);
  };

  const handleViewCarga = (carga) => {
    setSelectedCarga(carga);
  };

  const handleCloseModal = () => {
    setSelectedCarga(null);
    // Não limpar relatedTrips aqui, pois o useEffect já cuida disso
  };

  // Buscar viagens relacionadas e rastreamento quando uma carga é selecionada (filtro)
  useEffect(() => {
    if (selectedCarga && selectedCarga.id) {
      // Buscar viagens relacionadas à carga
      viagensAPI.listar({ carga_id: selectedCarga.id })
        .then((viagens) => {
          console.log(`📦 [AcompanhamentoCarga] Viagens retornadas pela API:`, viagens.length);
          if (viagens && viagens.length > 0) {
            // Filtrar apenas viagens que têm coordenadas do destinatário
            const viagensComCoordenadas = viagens.filter(v => {
              const lat = v.destinatario?.lat || v.destinatario_lat;
              const lng = v.destinatario?.lng || v.destinatario_lng;
              const temCoordenadas = lat && lng && parseFloat(lat) !== 0 && parseFloat(lng) !== 0;
              return temCoordenadas;
            });
            console.log(`✅ [AcompanhamentoCarga] Viagens filtradas: ${viagens.length}, com coordenadas: ${viagensComCoordenadas.length}`);
            setRelatedTrips(viagensComCoordenadas);
          } else {
            console.warn(`⚠️ [AcompanhamentoCarga] Nenhuma viagem encontrada para carga ${selectedCarga.id}`);
            setRelatedTrips([]);
          }
        })
        .catch((error) => {
          console.error('❌ [AcompanhamentoCarga] Erro ao buscar viagens relacionadas:', error);
          setRelatedTrips([]);
        });

      // Buscar última localização de rastreamento da carga selecionada
      rastreamentoAPI.buscarUltimaLocalizacao(selectedCarga.id)
        .then((ultimaLocalizacao) => {
          console.log(`🔍 [AcompanhamentoCarga] Resultado da busca para carga ${selectedCarga.id}:`, ultimaLocalizacao);
          
          if (ultimaLocalizacao) {
            // Normalizar campos de latitude/longitude
            const lat = ultimaLocalizacao.latitude || ultimaLocalizacao.lat;
            const lng = ultimaLocalizacao.longitude || ultimaLocalizacao.lng;
            
            if (lat && lng) {
              const latNum = parseFloat(lat);
              const lngNum = parseFloat(lng);
              
              // Validar coordenadas
              if (!isNaN(latNum) && !isNaN(lngNum) && latNum !== 0 && lngNum !== 0) {
                // Validar se está dentro de limites razoáveis (Brasil)
                if (latNum >= -35 && latNum <= 6 && lngNum >= -75 && lngNum <= -30) {
                  const trackingData = {
                    ...ultimaLocalizacao,
                    carga_id: selectedCarga.id,
                    latitude: latNum,
                    longitude: lngNum,
                  };
                  console.log(`✅ [AcompanhamentoCarga] Localização válida encontrada para carga ${selectedCarga.id}:`, trackingData);
                  setFilteredTrackingData([trackingData]);
                } else {
                  console.log(`⚠️ [AcompanhamentoCarga] Coordenadas fora dos limites do Brasil para carga ${selectedCarga.id}:`, { lat: latNum, lng: lngNum });
                  setFilteredTrackingData([]);
                }
              } else {
                console.log(`⚠️ [AcompanhamentoCarga] Coordenadas inválidas para carga ${selectedCarga.id}:`, { lat, lng });
                setFilteredTrackingData([]);
              }
            } else {
              console.log(`⚠️ [AcompanhamentoCarga] Carga ${selectedCarga.id} sem coordenadas válidas. Dados:`, ultimaLocalizacao);
              setFilteredTrackingData([]);
            }
          } else {
            console.log(`⚠️ [AcompanhamentoCarga] Nenhuma localização de rastreamento para carga ${selectedCarga.id}`);
            setFilteredTrackingData([]);
          }
        })
        .catch((error) => {
          console.log(`⚠️ [AcompanhamentoCarga] Carga ${selectedCarga.id} sem rastreamento disponível:`, error.message);
          setFilteredTrackingData([]);
        });
    } else {
      // Quando nenhuma carga está selecionada, limpar o filtro
      setRelatedTrips([]);
      setFilteredTrackingData([]);
    }
  }, [selectedCarga]);

  // Debug: Verificar se filteredTrackingData está sendo atualizado
  useEffect(() => {
    if (selectedCarga) {
      console.log(`🔍 [AcompanhamentoCarga] Estado atual - selectedCarga: ${selectedCarga.id}, filteredTrackingData:`, filteredTrackingData);
      console.log(`🔍 [AcompanhamentoCarga] filteredTrackingData.length:`, filteredTrackingData.length);
    }
  }, [selectedCarga, filteredTrackingData]);

  // Funções para o menu simplificado
  const handleBaixarPdfMdfe = async (carga) => {
    try {
      // Buscar o último evento MDF-e da carga
      const ultimoEvento = await mdfeAPI.buscarUltimoEvento(carga.id);
      
      if (!ultimoEvento || !ultimoEvento.mdfe_id) {
        dispatch(
          toastrActions.add({
            type: 'error',
            title: 'Erro',
            message: 'MDF-e não encontrado para esta carga.',
          })
        );
        return;
      }

      const blob = await mdfeAPI.baixarPdf(ultimoEvento.mdfe_id);
      
      // Criar link temporário para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MDFe-${ultimoEvento.mdfe_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'PDF baixado com sucesso.',
        })
      );
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao baixar PDF do MDF-e.',
        })
      );
    }
  };

  const handleBaixarXmlMdfe = async (carga) => {
    try {
      // Buscar o último evento MDF-e da carga
      const ultimoEvento = await mdfeAPI.buscarUltimoEvento(carga.id);
      
      if (!ultimoEvento || !ultimoEvento.mdfe_id) {
        dispatch(
          toastrActions.add({
            type: 'error',
            title: 'Erro',
            message: 'MDF-e não encontrado para esta carga.',
          })
        );
        return;
      }

      const blob = await mdfeAPI.baixarXml(ultimoEvento.mdfe_id);
      
      // Criar link temporário para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MDFe-${ultimoEvento.mdfe_id}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        toastrActions.add({
          type: 'success',
          title: 'Sucesso',
          message: 'XML baixado com sucesso.',
        })
      );
    } catch (error) {
      console.error('Erro ao baixar XML:', error);
      dispatch(
        toastrActions.add({
          type: 'error',
          title: 'Erro',
          message: 'Erro ao baixar XML do MDF-e.',
        })
      );
    }
  };

  // Funções vazias para evitar erros no ChargeItem
  const handleEmitirMdfe = () => {};
  const handleConsultarMdfe = () => {};
  const handleCancelarMdfe = () => {};
  const handleEncerrarMdfe = () => {};
  const handleSincronizarMdfe = () => {};
  const handleTogglePercurseLogs = () => {};

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <Container>
      <Card>
        <StaticDrawer
          title="ACOMPANHAMENTO DE CARGA"
          icon={<MdLocalShipping size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
          width="350px"
          mapActived={selectedCarga != null}
        >
          <Actions>
            <Action onClick={() => setFilterOpen(true)}>
              <MdFilterList size={20} color="#122d58" />
            </Action>
            <AnimatedSearchInput
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Buscar carga..."
            />
          </Actions>
          <CustomScrollbar style={{ height: '73%' }}>
            {filteredCargas.length > 0 ? (
              filteredCargas.map((carga) => (
                <ChargeItem
                  key={carga.id}
                  data={carga}
                  viewConvocation={handleViewCarga}
                  selected={selectedCarga?.id === carga.id}
                  selectedUser={null}
                  tooglePercurseLogs={handleTogglePercurseLogs}
                  onEmitirMdfe={handleEmitirMdfe}
                  onConsultarMdfe={handleConsultarMdfe}
                  onCancelarMdfe={handleCancelarMdfe}
                  onEncerrarMdfe={handleEncerrarMdfe}
                  onBaixarPdfMdfe={handleBaixarPdfMdfe}
                  onBaixarXmlMdfe={handleBaixarXmlMdfe}
                  onSincronizarMdfe={handleSincronizarMdfe}
                  useSimplifiedMenu={true}
                  MdfeMenuComponent={MdfeMenuSimplificado}
                />
              ))
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                Nenhuma carga encontrada para acompanhamento.
              </div>
            )}
          </CustomScrollbar>
        </StaticDrawer>
        <BoxMap>
          {selectedCarga && (
            <div
              style={{
                position: 'absolute',
                top: '18px',
                left: '18px',
                zIndex: 1000,
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#ffffff',
                boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.15)',
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              onClick={handleCloseModal}
            >
              <span style={{ fontSize: '18px', color: '#122d58' }}>×</span>
            </div>
          )}
          <MapConvocation 
            trips={selectedCarga ? relatedTrips : allTrips} 
            trackingData={selectedCarga ? filteredTrackingData : allTrackingData}
            key={selectedCarga ? `map-${selectedCarga.id}-${filteredTrackingData.length}` : `map-all-${allTrackingData.length}`} // Forçar re-render quando carga muda ou trackingData muda
          />
        </BoxMap>
      </Card>

      <ModalFilter
        open={filterOpen}
        handleClose={() => setFilterOpen(false)}
        handleFilter={handleFilter}
        resetFilter={handleResetFilter}
        form={formFilter}
        setForm={setFormFilter}
        data={cargas}
        options={{
          status: [],
          drivers: [],
          types: [],
          deliveryCities: [],
          deliveryStates: [],
        }}
        setOptions={() => {}}
      />

    </Container>
  );
}

